import React, { useEffect, useMemo, useState } from 'react';
import { Box, Radio, Typography } from '@mui/material';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import Global from '../../../../../Services/Common/Global';
import Commons from '../../../../../Services/Common/Common';
import { CreateColumn } from '../../../../../Services/agGrid/createColumn';
import {
  BuildFiRiskMatrixDerivedPatch,
  CalcFiRiskScoreString,
  ResolveLevelNmFromRiskMatrix,
} from './RiskAssessment_Schema';

const headerWrap = { wrapHeaderText: true, headerClass: 'ag-header-cell-wrap' };

const RISK_LEVEL_VALUES = ['상', '중', '하'];

/** 상=고위험(빨강), 중=중간(주황), 하=저위험(초록) */
const RISK_LEVEL_THEME = {
  상: {
    main: 'error.main',
    bgSelected: 'rgba(211, 47, 47, 0.14)',
    labelMuted: 'rgba(211, 47, 47, 0.72)',
  },
  중: {
    main: 'warning.main',
    bgSelected: 'rgba(237, 108, 2, 0.14)',
    labelMuted: 'rgba(183, 94, 0, 0.78)',
  },
  하: {
    main: 'success.main',
    bgSelected: 'rgba(46, 125, 50, 0.14)',
    labelMuted: 'rgba(46, 125, 50, 0.72)',
  },
};

const Pad2 = (n) => String(n).padStart(2, '0');

/** Date -> 로컬 YYYY-MM-DD (UTC 변환으로 하루 밀리는 것 방지) */
const ToLocalYmd = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${Pad2(d.getMonth() + 1)}-${Pad2(d.getDate())}`;
};

const NormalizeRsMethod = (v) =>
  String(v ?? '')
    .replace(/[·ㆍ]/g, '')
    .replace(/\s+/g, '')
    .trim();

const IsFrequencyIntensityMethod = (v) =>
  NormalizeRsMethod(v) === NormalizeRsMethod('빈도·강도법');

const RefreshFiRiskScoreCells = (params) => {
  params.api?.refreshCells?.({
    rowNodes: [params.node],
    columns: ['todayCurrentRisk', 'afterCurrentRisk'],
    force: true,
  });
};

const CollapseWs = (s) => String(s ?? '').replace(/\s+/g, '').trim();

/**
 * 가능성×중대성 점수가 matrixList 구간이면 해당 LevelNm, 아니면 빈 문자열(점수만 표시).
 */
const ResolveFiRiskBadgeDisplayLabel = (score, riskMatrixList) => {
  const n = Number(String(score ?? '').trim());
  if (!Number.isFinite(n)) return '';
  const matrix = Array.isArray(riskMatrixList) ? riskMatrixList : [];
  if (matrix.length === 0) return '';
  const levelNm = ResolveLevelNmFromRiskMatrix(score, matrix);
  return levelNm ? String(levelNm).trim() : '';
};

const RISK_SCORE_BADGE_THEME = {
  매우높음: { fg: '#C2410C', bg: '#FFF4E5', bd: '#FFD8A8' },
  높음: { fg: '#D97706', bg: '#FFF7ED', bd: '#FED7AA' },
  중간: { fg: '#1D4ED8', bg: '#EFF6FF', bd: '#BFDBFE' },
  낮음: { fg: '#166534', bg: '#ECFDF3', bd: '#BBF7D0' },
  매우낮음: { fg: '#01579B', bg: '#E1F5FE', bd: '#4FC3F7' },
  '매우 낮음': { fg: '#01579B', bg: '#E1F5FE', bd: '#4FC3F7' },
  보통: { fg: '#1565C0', bg: '#E8EAF6', bd: '#9FA8DA' },
  약간높음: { fg: '#E65100', bg: '#FFF3E0', bd: '#FFB74D' },
  '약간 높음': { fg: '#E65100', bg: '#FFF3E0', bd: '#FFB74D' },
};

const ResolveBadgeThemeForRiskLevel = (levelNm) => {
  if (!levelNm) return null;
  const raw = String(levelNm).trim();
  const compact = CollapseWs(raw);
  return (
    RISK_SCORE_BADGE_THEME[raw] ?? RISK_SCORE_BADGE_THEME[compact] ?? null
  );
};

const CenterCellStyle = {
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CreateRiskScoreBadgeCellRenderer = (riskMatrixList) => (params) => {
  const score = params.value;
  if (score == null || String(score).trim() === '') return '';
  const displayLabel = ResolveFiRiskBadgeDisplayLabel(
    score,
    riskMatrixList ?? []
  );
  const t =
    ResolveBadgeThemeForRiskLevel(displayLabel) ?? {
      fg: '#475467',
      bg: '#F2F4F7',
      bd: '#D0D5DD',
    };
  const text = displayLabel ? `${displayLabel} (${score})` : String(score);
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          border: '1px solid',
          borderColor: t.bd,
          bgcolor: t.bg,
          color: t.fg,
          fontSize: '0.86rem',
          fontWeight: 800,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

/**
 * 위험성 수준: 세로 라디오 (라벨 좌 / 라디오 우), 단계별 색상(상·중·하)
 */
export const RiskAssessmentRiskLevelCellRenderer = (params) => {
  const field = params.colDef?.field;
  if (!field) return null;
  const raw = params.value;
  const value =
    raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';

  const apply = (opt) => {
    if (opt === value) return;
    params.node.setDataValue(field, opt);
  };

  const name = `risk-level-${field}-${params.data?.clientKey ?? params.node?.id ?? params.rowIndex}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 0.25,
        gap: 0,
        width: '100%',
        minWidth: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {RISK_LEVEL_VALUES.map((opt) => {
        const selected = value === opt;
        const theme = RISK_LEVEL_THEME[opt];
        return (
          <Box
            key={opt}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                apply(opt);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              apply(opt);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              px: 0.5,
              py: 0.15,
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: selected ? theme.bgSelected : 'transparent',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: selected ? theme.main : theme.labelMuted,
                fontWeight: selected ? 700 : 500,
                fontSize: '0.8125rem',
                lineHeight: 1.2,
              }}
            >
              {opt}
            </Typography>
            <Radio
              size="small"
              name={name}
              checked={selected}
              value={opt}
              onChange={() => apply(opt)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                p: 0.25,
                color: theme.main,
                '&.Mui-checked': { color: theme.main },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

const RiskLevelColumnCommon = {
  width: 148,
  editable: false,
  sortable: false,
  filter: false,
  autoHeight: true,
  suppressHeaderMenuButton: true,
  cellRenderer: RiskAssessmentRiskLevelCellRenderer,
  cellStyle: {
    paddingLeft: 2,
    paddingRight: 2,
    paddingTop: 4,
    paddingBottom: 4,
  },
};

/** 날짜 입력(Date|string) → YYYY-MM-DD|null (agDateCellEditor 연동) */
const NormalizeDateValue = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return ToLocalYmd(v);
  const s = String(v).trim();
  if (!s) return null;
  return s.slice(0, 10);
};

/** 날짜 문자열 YYYY-MM-DD 정규화 (valueParser용) */
const NormalizeDateParser = (params) => NormalizeDateValue(params.newValue);

const EscapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 서버 상대경로 / 절대 URL / data URL → img src */
const ResolvePhotoSrc = (raw) => {
  const v = raw == null ? '' : String(raw).trim();
  if (!v) return '';
  const low = v.toLowerCase();
  if (
    low.startsWith('http://') ||
    low.startsWith('https://') ||
    low.startsWith('data:') ||
    low.startsWith('blob:')
  ) {
    return v;
  }
  const base = String(Global.file?.useUrl ?? '').replace(/\/?$/, '/');
  let path = v.replace(/\\/g, '/');
  if (base) {
    path = path.replace(new RegExp(`^${EscapeRegex(base)}files/`, 'i'), '');
  }
  path = path.replace(/^\/?files\//i, '');
  const enc = Commons.EncodeUrl(path);
  if (!enc) return '';
  const fileBase = Global.file?.useUrl;
  if (!fileBase) return '';
  return `${String(fileBase).replace(/\/?$/, '/')}files/${enc}`;
};

/**
 * 개선 전/후 사진: 빈 셀은 ‘사진 등록’ 플레이스홀더, 값이 있으면 그리드에 썸네일 표시
 * (로컬 선택 시 data URL 저장 — 저장 API 연동 시 서버 경로로 치환 가능)
 */
export const RiskAssessmentPhotoCellRenderer = (params) => {
  const field = params.colDef?.field;
  const [loadError, setLoadError] = useState(false);
  const raw = params.value;
  const src = useMemo(() => ResolvePhotoSrc(raw), [raw]);

  useEffect(() => {
    setLoadError(false);
  }, [src]);

  const onRequestPhoto =
    params.colDef?.cellRendererParams?.onRequestPhoto ?? params.onRequestPhoto;
  const HandleRequestPhoto = () => {
    const clientKey = params.data?.clientKey;
    if (!clientKey || !field) return;
    onRequestPhoto?.({ clientKey, field });
  };

  const showPlaceholder = !src || loadError;

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 0.25,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {showPlaceholder ? (
        <Box
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              HandleRequestPhoto();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            HandleRequestPhoto();
          }}
          sx={{
            width: '100%',
            maxWidth: 132,
            minHeight: 76,
            px: 1,
            py: 1,
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            bgcolor: 'grey.50',
            cursor: 'pointer',
          }}
        >
          <AddAPhotoOutlinedIcon
            sx={{ fontSize: 28, color: 'primary.light' }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'primary.light', fontWeight: 600, lineHeight: 1.2 }}
          >
            사진 등록
          </Typography>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              HandleRequestPhoto();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            HandleRequestPhoto();
          }}
          sx={{
            width: '100%',
            maxWidth: 132,
            height: 76,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            bgcolor: 'grey.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={src}
            alt=""
            onError={() => setLoadError(true)}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const DateValueFormatter = (p) => {
  const v = p.value;
  if (v == null || v === '') return '';
  // Date 객체/문자열 모두 YYYY-MM-DD로 표시
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return ToLocalYmd(v);
  }
  const s = String(v).trim();
  if (!s) return '';
  // 이미 'YYYY-MM-DD' 또는 'YYYY-MM-DD...' 형태면 앞 10자리만 사용
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // 그 외(예: 'Wed Mar 25 ...')는 그대로 두되, 최대한 앞 10자리 자르지 않음
  return s;
};

/** 장문: 팝업으로 전체 편집, 셀 표시는 약 4줄 — 스타일은 RiskAssessment_GridOverrides.css */
const MultiLineTextCol = {
  wrapText: true,
  cellClass: 'ag-cell-wrap-text risk-assessment-ml-2',
  cellStyle: {
    paddingTop: 0.5,
    paddingBottom: 0.5,
  },
  tooltipValueGetter: (p) => {
    const v = p.value;
    if (v == null || String(v).trim() === '') return null;
    return String(v);
  },
  cellEditor: 'agLargeTextCellEditor',
  cellEditorPopup: true,
  cellEditorParams: {
    rows: 10,
    cols: 50,
  },
};

/**
 * 위험성평가 실시 — 세부항목 그리드 컬럼
 * 체크박스·checkboxProps: ACS_BASIC_01_ColumnDefs getColumnDefs1 과 동일
 * rsdCd: accidentSeq 와 같이 숨김 컬럼으로만 보관
 */
export const getColumnDefs1 = (props = {}) => {
  const isFi = IsFrequencyIntensityMethod(props.rsMethod);
  const fiRiskBadgeRenderer = CreateRiskScoreBadgeCellRenderer(
    props.riskMatrixList ?? []
  );

  const beforeRiskCols = isFi
    ? [
        {
          headerName: '현재 위험성',
          marryChildren: true,
          headerClass: 'ag-center-header',
          children: [
            {
              field: 'todayPossibility',
              headerName: '가능성(빈도)',
              width: 130,
              editable: true,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: ['1', '2', '3', '4', '5'] },
              valueSetter: (params) => {
                const next = String(params.newValue ?? '').trim();
                params.data.todayPossibility = next;
                Object.assign(
                  params.data,
                  BuildFiRiskMatrixDerivedPatch(
                    params.data,
                    props.riskMatrixList ?? []
                  )
                );
                RefreshFiRiskScoreCells(params);
                return true;
              },
            },
            {
              field: 'todayImportance',
              headerName: '중대성(강도)',
              width: 130,
              editable: true,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: ['1', '2', '3', '4'] },
              valueSetter: (params) => {
                const next = String(params.newValue ?? '').trim();
                params.data.todayImportance = next;
                Object.assign(
                  params.data,
                  BuildFiRiskMatrixDerivedPatch(
                    params.data,
                    props.riskMatrixList ?? []
                  )
                );
                RefreshFiRiskScoreCells(params);
                return true;
              },
            },
            {
              field: 'todayCurrentRisk',
              headerName: '위험성',
              width: 130,
              editable: false,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              valueGetter: (params) =>
                CalcFiRiskScoreString(
                  params.data?.todayPossibility,
                  params.data?.todayImportance
                ),
              cellRenderer: fiRiskBadgeRenderer,
            },
          ],
        },
      ]
    : [
        {
          field: 'riskLevelJudgment',
          headerName: '위험성 수준판단',
          ...RiskLevelColumnCommon,
        },
      ];

  const afterRiskCols = isFi
    ? [
        {
          headerName: '개선 후 위험성',
          marryChildren: true,
          headerClass: 'ag-center-header',
          children: [
            {
              field: 'afterPossibility',
              headerName: '가능성(빈도)',
              width: 130,
              editable: true,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: ['1', '2', '3', '4', '5'] },
              valueSetter: (params) => {
                const next = String(params.newValue ?? '').trim();
                params.data.afterPossibility = next;
                Object.assign(
                  params.data,
                  BuildFiRiskMatrixDerivedPatch(
                    params.data,
                    props.riskMatrixList ?? []
                  )
                );
                RefreshFiRiskScoreCells(params);
                return true;
              },
            },
            {
              field: 'afterImportance',
              headerName: '중대성(강도)',
              width: 130,
              editable: true,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: ['1', '2', '3', '4'] },
              valueSetter: (params) => {
                const next = String(params.newValue ?? '').trim();
                params.data.afterImportance = next;
                Object.assign(
                  params.data,
                  BuildFiRiskMatrixDerivedPatch(
                    params.data,
                    props.riskMatrixList ?? []
                  )
                );
                RefreshFiRiskScoreCells(params);
                return true;
              },
            },
            {
              field: 'afterCurrentRisk',
              headerName: '위험성',
              width: 130,
              editable: false,
              headerClass: 'ag-center-header',
              cellStyle: CenterCellStyle,
              valueGetter: (params) =>
                CalcFiRiskScoreString(
                  params.data?.afterPossibility,
                  params.data?.afterImportance
                ),
              cellRenderer: fiRiskBadgeRenderer,
            },
          ],
        },
      ]
    : [
        {
          field: 'riskAfterLevelJudgment',
          headerName: '개선 후 위험성',
          ...RiskLevelColumnCommon,
        },
      ];

  return [
    CreateColumn('no', 'NO', 'center', 80, false, false, false, 'checkbox', {
      checkboxProps: {
        DeleteNewRow: props.DeleteNewRow,
        RestoreRow: props.RestoreRow,
        gridRef: props.gridRef,
        rowData: props.rowData,
        selectedRowNosRef: props.selectedRowNosRef,
        lastCheckedIndexRef: props.lastCheckedIndexRef,
        handleRowSelection: props.handleRowSelection,
      },
      maxWidth: 100,
      ...headerWrap,
    }),
    CreateColumn('rsdCd', 'rsdCd', 'center', 80, false, false, true, 'text', {
      minWidth: 130,
      ...headerWrap,
    }),
    {
      field: 'categoryComment',
      headerName: '위험발생 상황 및 결과',
      minWidth: 300,
      flex: 1.2,
      editable: true,
      ...MultiLineTextCol,
    },
    {
      field: 'todaySafety',
      headerName: '현재의 안전보건 조치',
      minWidth: 300,
      flex: 1,
      editable: true,
      ...MultiLineTextCol,
    },
    ...beforeRiskCols,
    {
      field: 'riskImageFilePath',
      headerName: '개선 전 사진',
      width: 180,
      minWidth: 160,
      editable: false,
      sortable: false,
      filter: false,
      cellStyle: { paddingLeft: 6, paddingRight: 6 },
      cellRenderer: RiskAssessmentPhotoCellRenderer,
      cellRendererParams: {
        onRequestPhoto: props.onRequestPhoto,
      },
      tooltipValueGetter: (p) => {
        const v = p.value;
        if (v == null || String(v).trim() === '') return '클릭하여 사진 등록';
        return String(v).length > 120
          ? `${String(v).slice(0, 120)}…`
          : String(v);
      },
    },
    {
      field: 'reductionMeasures',
      headerName: '위험성 감소대책',
      minWidth: 300,
      flex: 1.2,
      editable: false,
      ...MultiLineTextCol,
      tooltipValueGetter: () =>
        '클릭하여 위험성 감소대책을 선택·직접 추가하세요.',
    },
    {
      field: 'afterRiskImageFilePath',
      headerName: '개선 후 사진',
      width: 180,
      minWidth: 160,
      editable: false,
      sortable: false,
      filter: false,
      cellStyle: { paddingLeft: 6, paddingRight: 6 },
      cellRenderer: RiskAssessmentPhotoCellRenderer,
      cellRendererParams: {
        onRequestPhoto: props.onRequestPhoto,
      },
      tooltipValueGetter: (p) => {
        const v = p.value;
        if (v == null || String(v).trim() === '') return '클릭하여 사진 등록';
        return String(v).length > 120
          ? `${String(v).slice(0, 120)}…`
          : String(v);
      },
    },
    {
      headerName: '감소대책실행',
      marryChildren: true,
      headerClass: 'ag-center-header',
      children: [
        {
          field: 'gaesunExDate',
          headerName: '개선예정일',
          width: 120,
          editable: true,
          headerClass: 'ag-center-header',
          cellStyle: CenterCellStyle,
          cellEditor: 'agDateCellEditor',
          cellEditorParams: {
            format: 'yyyy-MM-dd',
          },
          valueFormatter: DateValueFormatter,
          valueParser: NormalizeDateParser,
        },
        {
          field: 'gaesunComDate',
          headerName: '개선완료일',
          width: 120,
          editable: true,
          headerClass: 'ag-center-header',
          cellStyle: CenterCellStyle,
          cellEditor: 'agDateCellEditor',
          cellEditorParams: {
            format: 'yyyy-MM-dd',
          },
          valueFormatter: DateValueFormatter,
          valueParser: NormalizeDateParser,
        },
      ],
    },
    ...afterRiskCols,
    {
      field: 'condolenceNo',
      headerName: '조문번호',
      width: 120,
      editable: true,
      cellStyle: CenterCellStyle,
    },
    {
      field: 'relatedEvidence',
      headerName: '관련근거(법적기준)',
      minWidth: 400,
      flex: 1,
      editable: true,
      ...MultiLineTextCol,
    },
  ];
};
